import { useState } from 'react';
import { ApiError, reconApi, teampaceApi, type TeampaceLinkView, type TeampaceMonthReport, type TeampaceReviewRow } from '../../api/client';
import { Button, Card, Chip, Field, LoadError, Loading, Notice } from '../../components/ui';
import { fullWhen, plural } from '../../lib/format';
import { useData } from '../../lib/useData';

const HOW: Record<string, string> = { created: 'Made by Stitch', matched: 'Matched by billing email, domain or Stitch reference', chosen: 'Chosen by Stitch staff' };
const CHARGE_STATE: Record<string, string> = { paid: 'Paid', failed: 'Failed', processing: 'Going through', not_charged: 'Not charged', refunded: 'Refunded' };
const money = (cents: number) => `${cents < 0 ? '−' : ''}$${(Math.abs(cents) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Stitch Admin → Customers → a company → Teampace (Billing 1, 3089): the company's Teampace client
 * (Ceexo's system of record for customers, invoices and payments), how it was found, what differs in
 * Teampace (changes there aren't copied back), and Retry or Link to another client.
 */
export function TeampaceCard({ customerId }: { customerId: string }) {
  const data = useData(() => teampaceApi.view(customerId), [customerId], { live: false });
  const [busy, setBusy] = useState(false);
  const [other, setOther] = useState('');
  const [said, setSaid] = useState<{ tone: 'ok' | 'crit'; text: string } | null>(null);

  async function run(fn: () => Promise<TeampaceLinkView>, ok: string) {
    setBusy(true);
    setSaid(null);
    try {
      const v = await fn();
      data.reload();
      setSaid(v.link?.state === 'failed' ? { tone: 'crit', text: v.link.lastError ?? 'It didn’t work; Stitch tries again.' } : { tone: 'ok', text: ok });
    } catch (err) {
      setSaid({ tone: 'crit', text: err instanceof ApiError ? err.message : 'That didn’t work. Try again.' });
    } finally {
      setBusy(false);
    }
  }

  if (data.error) {
    return (
      <Card title="Teampace">
        <div className="card-body">
          <LoadError error={data.error} retry={data.reload} />
        </div>
      </Card>
    );
  }
  const v = data.data;
  if (!v) {
    return (
      <Card title="Teampace">
        <div className="card-body">
          <Loading label="Loading Teampace" />
        </div>
      </Card>
    );
  }
  const l = v.link;
  const chip = !v.configured ? <Chip tone="neutral">Not set up</Chip> : !l ? <Chip tone="neutral">Not linked</Chip> : l.state === 'linked' ? <Chip tone="ok">Linked</Chip> : l.state === 'review' ? <Chip tone="warn">Needs a choice</Chip> : l.state === 'failed' ? <Chip tone="crit">Not in step</Chip> : <Chip tone="brand">Waiting</Chip>;
  const otherId = Number(other.trim());
  return (
    <Card title="Teampace" action={chip}>
      <div className="card-body">
        {!v.configured ? (
          <p className="muted">Teampace isn’t set up on this portal yet (TEAMPACE_API_URL and TEAMPACE_API_KEY). Companies are linked once it is.</p>
        ) : (
          <>
            {l?.clientId && v.client ? (
              <p>
                Teampace client <strong>{v.client.name || `#${v.client.id}`}</strong> <span className="muted">(#{v.client.id})</span>
                {v.client.url && (
                  <>
                    {' · '}
                    <a href={v.client.url} target="_blank" rel="noreferrer">
                      Open in Teampace
                    </a>
                  </>
                )}
              </p>
            ) : (
              <p className="muted">{l ? 'Not linked to a Teampace client yet.' : v.syncFrom ? 'Not linked yet: Stitch links it within a minute.' : 'Not linked. Link it here, or with Link current companies on System → Teampace.'}</p>
            )}
            {l?.how && l.clientId && <p className="muted small">{HOW[l.how]}{l.linkedAt ? `, ${fullWhen(l.linkedAt)}` : ''} · {plural(l.contacts, 'billing contact')} in Teampace{l.checkedAt ? ` · checked ${fullWhen(l.checkedAt)}` : ''}</p>}
            {l?.state === 'failed' && (
              <Notice tone="crit" title="Not in step with Teampace">
                {l.lastError} Stitch tries again {fullWhen(l.nextAt)} ({plural(l.attempts, 'try', 'tries')} so far).
              </Notice>
            )}
            {l?.state === 'review' && (
              <Notice tone="warn" title="Which Teampace client is this company?">
                <p>More than one client could be it, so Stitch didn’t guess.</p>
                <ul className="plain">
                  {l.candidates.map((c) => (
                    <li key={c.id}>
                      <strong>{c.name}</strong> <span className="muted">(#{c.id}, {c.why})</span>{' '}
                      <Button size="small" variant="secondary" disabled={busy} onClick={() => void run(() => teampaceApi.link(customerId, c.id), `Linked to ${c.name}.`)}>
                        This one
                      </Button>
                    </li>
                  ))}
                </ul>
                <Button size="small" variant="ghost" disabled={busy} onClick={() => void run(() => teampaceApi.link(customerId, null), 'A new Teampace client was made.')}>
                  None of them: make a new client
                </Button>
              </Notice>
            )}
            {l && l.differs.length > 0 && (
              <Notice tone="warn" title="Differs from Teampace">
                <p className="small">Changed in Teampace (Stitch doesn’t copy changes back). Fix it in Teampace, or change it in Stitch and Stitch sends it.</p>
                <table className="table compact">
                  <thead>
                    <tr>
                      <th>Field</th>
                      <th>Stitch</th>
                      <th>Teampace</th>
                    </tr>
                  </thead>
                  <tbody>
                    {l.differs.map((d) => (
                      <tr key={d.field}>
                        <td>{d.field}</td>
                        <td>{d.stitch || '—'}</td>
                        <td>{d.teampace || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Notice>
            )}
            {v.documents.length > 0 && (
              <>
                <h3 className="small">Sent to Teampace</h3>
                <table className="table compact">
                  <thead>
                    <tr>
                      <th>Invoice</th>
                      <th>What</th>
                      <th>State</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {v.documents.map((d) => (
                      <tr key={d.key}>
                        <td>{d.number || '—'}</td>
                        <td>{d.kind === 'invoice' ? 'Invoice' : d.kind === 'credit_note' ? 'Credit note' : 'Void'}</td>
                        <td>
                          {d.state === 'sent' ? (
                            <span>
                              <Chip tone="ok" small>
                                Sent
                              </Chip>{' '}
                              <span className="muted small">
                                {d.note ?? `${d.teampaceId ? `#${d.teampaceId}` : ''}${d.kind === 'invoice' && d.fileAttached ? ', PDF attached' : ''}${d.sentAt ? ` · ${fullWhen(d.sentAt)}` : ''}`}
                              </span>
                            </span>
                          ) : (
                            <span>
                              <Chip tone={d.state === 'failed' ? 'crit' : 'brand'} small>
                                {d.state === 'failed' ? 'Not sent yet' : 'Waiting'}
                              </Chip>{' '}
                              <span className="muted small">{d.lastError ? `${d.lastError}. Next try ${fullWhen(d.nextAt)}.` : ''}</span>
                            </span>
                          )}
                        </td>
                        <td>
                          {d.state !== 'sent' && (
                            <Button size="small" variant="ghost" disabled={busy} onClick={() => void run(async () => (await teampaceApi.retryDocument(customerId, d.key), teampaceApi.view(customerId)), 'Tried again.')}>
                              Retry
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
            {v.payments && v.payments.unpaid.length > 0 && (
              <Notice tone={v.payments.unpaid.some((u) => u.blockedAt) ? 'crit' : 'warn'} title="Unpaid invoices">
                <ul className="plain">
                  {v.payments.unpaid.map((u) => (
                    <li key={u.invoiceId}>
                      <strong>{u.number}</strong> {money(u.openCents)} · {u.why === 'failed' ? `payment failed ${plural(u.failures, 'time')}${u.lastReason ? ` (${u.lastReason})` : ''}` : 'past due'} since {fullWhen(u.startedAt)}
                      {u.suspendedAt ? ' · billing account suspended' : u.warnedAt ? ' · final notice sent' : u.blockedAt ? ' · new seats and numbers blocked' : ''}
                    </li>
                  ))}
                </ul>
              </Notice>
            )}
            {v.payments && v.payments.charges.length > 0 && (
              <>
                <h3 className="small">Payments</h3>
                <table className="table compact">
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>Invoice</th>
                      <th>How</th>
                      <th className="num">Amount</th>
                      <th>Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {v.payments.charges.map((c) => (
                      <tr key={c.id}>
                        <td>{fullWhen(c.at)}</td>
                        <td>{c.number}</td>
                        <td>
                          {c.how}
                          {c.card ? <span className="muted small"> · {c.card}</span> : null}
                        </td>
                        <td className="num">{money(c.amountCents)}</td>
                        <td>
                          <Chip tone={c.state === 'paid' ? 'ok' : c.state === 'failed' ? 'crit' : c.state === 'not_charged' ? 'warn' : c.state === 'processing' ? 'brand' : 'neutral'} small>
                            {CHARGE_STATE[c.state]}
                          </Chip>
                          <span className="muted small">
                            {c.reason ? ` ${c.reason}` : ''}
                            {c.refundedCents > 0 ? ` ${money(c.refundedCents)} refunded` : ''}
                            {c.teampaceId ? ` · #${c.teampaceId}` : ''}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
            {said && (
              <Notice tone={said.tone} title={said.tone === 'ok' ? 'Done' : 'That didn’t work'}>
                {said.text}
              </Notice>
            )}
            <div className="row-actions">
              <Button variant="secondary" icon="refresh" disabled={busy} onClick={() => void run(() => teampaceApi.retry(customerId), 'In step with Teampace.')}>
                {l ? 'Retry now' : 'Link now'}
              </Button>
              <Field label="Link to another client (its number in Teampace)" value={other} inputMode="numeric" onChange={(e) => setOther(e.target.value.replace(/[^0-9]/g, ''))} />
              <Button variant="ghost" disabled={busy || !(otherId > 0)} onClick={() => void run(() => teampaceApi.link(customerId, otherId), `Linked to Teampace client #${otherId}.`)}>
                Link
              </Button>
            </div>
          </>
        )}
      </div>
    </Card>
  );
}

/**
 * Stitch Admin → System → Teampace: whether it's set up, syncing new companies (from when), and Link
 * current companies: what Stitch would do for each company without a client yet, for staff to check
 * before anything is written.
 */
export function TeampaceSystemCard() {
  const status = useData(() => teampaceApi.status(), [], { live: false });
  const [rows, setRows] = useState<TeampaceReviewRow[] | null>(null);
  const [choice, setChoice] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<{ tone: 'ok' | 'crit'; text: string } | null>(null);
  const s = status.data;

  const load = async () => {
    setBusy(true);
    setSaid(null);
    try {
      const r = (await teampaceApi.review()).rows;
      setRows(r);
      // Sure matches are linked unless changed; the rest wait for a choice (skipped until then).
      setChoice(Object.fromEntries(r.map((x) => [x.customerId, x.proposal === 'link' ? `link:${x.candidates[0]!.id}` : x.proposal === 'create' ? 'create' : 'skip'])));
    } catch (err) {
      setSaid({ tone: 'crit', text: err instanceof ApiError ? err.message : 'The list couldn’t be made. Try again.' });
    } finally {
      setBusy(false);
    }
  };

  const apply = async () => {
    if (!rows) return;
    setBusy(true);
    setSaid(null);
    try {
      const choices = rows.map((r) => {
        const c = choice[r.customerId] ?? 'skip';
        return c.startsWith('link:') ? { customerId: r.customerId, action: 'link' as const, clientId: Number(c.slice(5)) } : { customerId: r.customerId, action: c as 'create' | 'skip' };
      });
      const out = await teampaceApi.applyReview(choices);
      setSaid({ tone: out.failed.length ? 'crit' : 'ok', text: `${plural(out.linked, 'company', 'companies')} linked, ${out.created} new ${out.created === 1 ? 'client' : 'clients'} made, ${out.skipped} left for later${out.failed.length ? `, ${out.failed.length} not done (Stitch tries those again)` : ''}.` });
      setRows(null);
      status.reload();
    } catch (err) {
      setSaid({ tone: 'crit', text: err instanceof ApiError ? err.message : 'That didn’t work. Try again.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title="Teampace" action={s ? <Chip tone={s.configured ? (s.syncFrom ? 'ok' : 'warn') : 'neutral'}>{!s.configured ? 'Not set up' : s.syncFrom ? 'Syncing' : 'Not syncing yet'}</Chip> : undefined}>
      <div className="card-body">
        {status.error ? (
          <LoadError error={status.error} retry={status.reload} />
        ) : !s ? (
          <Loading label="Loading Teampace" />
        ) : !s.configured ? (
          <p className="muted">Ceexo’s system of record for customers, invoices and payments. Set TEAMPACE_API_URL and TEAMPACE_API_KEY on the portal (EasyPanel), then come back here.</p>
        ) : (
          <>
            <p className="muted small">
              {s.syncFrom ? `New companies get their Teampace client on their own, since ${fullWhen(s.syncFrom)}.` : 'New companies aren’t linked on their own yet.'} {plural(s.counts.linked ?? 0, 'company', 'companies')} linked of {s.companies}
              {s.counts.review ? `, ${s.counts.review} need a choice` : ''}
              {s.counts.failed ? `, ${s.counts.failed} not in step` : ''}.
            </p>
            {said && (
              <Notice tone={said.tone} title={said.tone === 'ok' ? 'Done' : 'That didn’t work'}>
                {said.text}
              </Notice>
            )}
            <label className="check">
              <input
                type="checkbox"
                checked={s.push.push}
                disabled={busy}
                onChange={(e) => {
                  const on = e.target.checked;
                  setBusy(true);
                  void teampaceApi
                    .setPush(on)
                    .then(() => status.reload())
                    .catch((err) => setSaid({ tone: 'crit', text: err instanceof ApiError ? err.message : 'That didn’t work.' }))
                    .finally(() => setBusy(false));
                }}
              />{' '}
              Send issued invoices, credit notes and voids to Teampace (with the PDF)
            </label>
            <p className="muted small">
              {s.push.push
                ? `On: what’s issued from ${s.push.pushFrom ? fullWhen(s.push.pushFrom) : 'now'} goes to Teampace within a minute. ${s.documents.sent ?? 0} sent, ${s.documents.waiting ?? 0} waiting, ${s.documents.failed ?? 0} not sent yet.`
                : 'Off until billing goes live (the shadow run sends nothing). Turned on, only what’s issued from then goes.'}
            </p>
            <label className="check">
              <input
                type="checkbox"
                checked={s.payments.on}
                disabled={busy}
                onChange={(e) => {
                  const on = e.target.checked;
                  setBusy(true);
                  void teampaceApi
                    .setPayments(on)
                    .then(() => status.reload())
                    .catch((err) => setSaid({ tone: 'crit', text: err instanceof ApiError ? err.message : 'That didn’t work.' }))
                    .finally(() => setBusy(false));
                }}
              />{' '}
              Take payments (autopay on the due date, and Pay now in the portal)
            </label>
            <p className="muted small">
              {s.payments.on
                ? `On: invoices issued from ${s.payments.from ? fullWhen(s.payments.from) : 'now'} are charged through Teampace’s card plugin. In the last 30 days: ${s.payments.counts.succeeded ?? 0} paid, ${s.payments.counts.failed ?? 0} failed, ${(s.payments.counts.pending ?? 0) + (s.payments.counts.unknown ?? 0) + (s.payments.counts.sending ?? 0)} going through. ${plural(s.payments.counts.unpaid ?? 0, 'invoice')} unpaid, ${s.payments.counts.blocked ?? 0} blocking new seats and numbers.`
                : 'Off until billing goes live: nobody is charged (the shadow run included). Turned on, only invoices issued from then are charged.'}{' '}
              Teampace retries a failed card on days {s.payments.retryDays.join(', ')} {s.payments.retryFrom === 'teampace' ? '(from Teampace)' : '(the default until Teampace says)'}; Stitch never retries on top.
              {s.payments.on && !s.push.push ? ' Invoices aren’t being sent to Teampace, so nothing can be charged yet.' : ''}
            </p>
            <div className="row-actions">
              {!s.syncFrom && (
                <Button
                  variant="primary"
                  disabled={busy}
                  onClick={() => {
                    setBusy(true);
                    void teampaceApi
                      .start()
                      .then(() => status.reload())
                      .catch((err) => setSaid({ tone: 'crit', text: err instanceof ApiError ? err.message : 'That didn’t work.' }))
                      .finally(() => setBusy(false));
                  }}
                >
                  Sync new companies
                </Button>
              )}
              <Button variant="secondary" disabled={busy} onClick={() => void load()}>
                Link current companies…
              </Button>
            </div>
            {rows && (
              <>
                {rows.length === 0 ? (
                  <p className="muted">Every company has its Teampace client.</p>
                ) : (
                  <table className="table compact">
                    <thead>
                      <tr>
                        <th>Company</th>
                        <th>Stitch would</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r.customerId}>
                          <td>{r.company}</td>
                          <td>
                            <select aria-label={`What to do for ${r.company}`} value={choice[r.customerId] ?? 'skip'} onChange={(e) => setChoice((c) => ({ ...c, [r.customerId]: e.target.value }))}>
                              {r.candidates.map((x) => (
                                <option key={x.id} value={`link:${x.id}`}>
                                  Link to {x.name} (#{x.id}, {x.why})
                                </option>
                              ))}
                              <option value="create">Make a new Teampace client</option>
                              <option value="skip">Leave for later</option>
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {rows.length > 0 && (
                  <div className="row-actions">
                    <Button variant="primary" disabled={busy} onClick={() => void apply()}>
                      Apply to {plural(rows.filter((r) => (choice[r.customerId] ?? 'skip') !== 'skip').length, 'company', 'companies')}
                    </Button>
                    <Button variant="ghost" disabled={busy} onClick={() => setRows(null)}>
                      Cancel
                    </Button>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </Card>
  );
}

const KIND: Record<string, string> = {
  missing: 'Not in Teampace',
  only_in_teampace: 'Only in Teampace',
  total: 'Totals differ',
  void: 'Void differs',
  credit_note: 'Credit note',
  payment: 'Payments differ',
  credit_used: 'Account credit'
};

/** The previous month, as YYYY-MM (the one usually signed off). */
function lastMonth(): string {
  const d = new Date();
  const m = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() - 1, 1));
  return m.toISOString().slice(0, 7);
}

/**
 * Stitch Admin → System → Reconciliation (Billing 5, 3093): each night's comparison of Stitch's issued
 * invoices, payments, credit notes and voids with Teampace's, what still differs (never fixed by itself),
 * Run now, and a month's report, Stitch and Teampace side by side, for accounting to sign off.
 */
export function TeampaceReconCard() {
  const data = useData(() => reconApi.status(), [], { live: false });
  const [month, setMonth] = useState(lastMonth());
  const [report, setReport] = useState<TeampaceMonthReport | null>(null);
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<{ tone: 'ok' | 'crit'; text: string } | null>(null);
  const v = data.data;
  const last = v?.runs[0];

  async function act<T>(fn: () => Promise<T>, then: (x: T) => void) {
    setBusy(true);
    setSaid(null);
    try {
      then(await fn());
    } catch (err) {
      setSaid({ tone: 'crit', text: err instanceof ApiError ? err.message : 'That didn’t work. Try again.' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="Reconciliation" action={v ? <Chip tone={!v.configured ? 'neutral' : v.open.length ? 'warn' : 'ok'}>{!v.configured ? 'Not set up' : v.open.length ? `${v.open.length} to look at` : 'In step'}</Chip> : undefined}>
      <div className="card-body">
        {data.error ? (
          <LoadError error={data.error} retry={data.reload} />
        ) : !v ? (
          <Loading label="Loading reconciliation" />
        ) : (
          <>
            <p className="muted small">
              Each night from 5 am Eastern, Stitch compares this month’s (and last month’s, on the 1st) issued invoices, payments, credit notes and voids with Teampace’s. Nothing is fixed by itself: fix it in Teampace, or retry
              sending from the company’s Teampace card, and the next run clears it. Accounting gets an email each morning while anything is open.
              {last ? ` Last run: ${fullWhen(last.startedAt)}, ${last.status === 'done' ? `${plural(last.counts.invoices ?? 0, 'invoice')} compared` : last.status === 'failed' ? `didn’t finish (${last.error ?? 'Teampace didn’t answer'}); it’s tried again within the hour` : 'running'}.` : ' It hasn’t run yet.'}
            </p>
            {v.open.length > 0 && (
              <table className="table compact">
                <thead>
                  <tr>
                    <th>Company</th>
                    <th>Invoice</th>
                    <th>What</th>
                    <th className="num">Stitch</th>
                    <th className="num">Teampace</th>
                    <th>Since</th>
                  </tr>
                </thead>
                <tbody>
                  {v.open.map((d) => (
                    <tr key={d.key}>
                      <td>{d.customerId ? <a href={`/admin/customers/${encodeURIComponent(d.customerId)}`}>{d.company || 'A company'}</a> : '—'}</td>
                      <td>{d.number || (d.teampaceId ? `Teampace #${d.teampaceId}` : '—')}</td>
                      <td>
                        <strong>{KIND[d.kind] ?? d.kind}</strong>
                        <div className="muted small">{d.detail}</div>
                      </td>
                      <td className="num">{d.stitchCents === null ? '—' : money(d.stitchCents)}</td>
                      <td className="num">{d.teampaceCents === null ? '—' : money(d.teampaceCents)}</td>
                      <td>{fullWhen(d.firstSeenAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <div className="row-actions">
              <Button variant="secondary" icon="refresh" disabled={busy || !v.configured} onClick={() => void act(() => reconApi.run(), () => (data.reload(), setSaid({ tone: 'ok', text: 'Compared with Teampace just now.' })))}>
                Run now
              </Button>
            </div>
            <h3 className="small">Month-end report</h3>
            <div className="row-actions">
              <Field label="Month" type="month" value={month} onChange={(e) => (setMonth(e.target.value), setReport(null))} />
              <Button variant="secondary" disabled={busy || !v.configured || !/^\d{4}-\d{2}$/.test(month)} onClick={() => void act(() => reconApi.month(month), setReport)}>
                Show
              </Button>
            </div>
            {report && (
              <>
                <table className="table compact">
                  <thead>
                    <tr>
                      <th>{report.month.slice(0, 7)}</th>
                      <th className="num">Stitch</th>
                      <th className="num">Teampace</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Invoices</td>
                      <td className="num">{report.invoices.stitch}</td>
                      <td className="num">{report.invoices.teampace}</td>
                      <td />
                    </tr>
                    {report.rows.map((r) => (
                      <tr key={r.key}>
                        <td>{r.label}</td>
                        <td className="num">{money(r.stitchCents)}</td>
                        <td className="num">{money(r.teampaceCents)}</td>
                        <td>{r.stitchCents === r.teampaceCents ? <Chip tone="ok" small>Matches</Chip> : <Chip tone="crit" small>{money(r.teampaceCents - r.stitchCents)}</Chip>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {report.signoff ? (
                  <Notice tone={report.signoff.matches ? 'ok' : 'warn'} title="Signed off">
                    By {report.signoff.byName}, {fullWhen(report.signoff.at)}{report.signoff.matches ? '' : ', with differences'}. The report is kept as it was then.
                  </Notice>
                ) : (
                  <div className="row-actions">
                    <Button variant="primary" disabled={busy || !report.ended} onClick={() => void act(() => reconApi.signOff(month), (r) => (setReport(r), setSaid({ tone: 'ok', text: `Signed off ${month}.` })))}>
                      Sign off {month}
                    </Button>
                    <span className="muted small">{!report.ended ? 'A month is signed off once it has ended.' : report.matches ? 'Everything matches.' : 'Some rows differ; signing off keeps them on record as they are.'}</span>
                  </div>
                )}
              </>
            )}
            {said && (
              <Notice tone={said.tone} title={said.tone === 'ok' ? 'Done' : 'That didn’t work'}>
                {said.text}
              </Notice>
            )}
          </>
        )}
      </div>
    </Card>
  );
}
